import { NetworkError } from 'common/errors'
import { Cancelled } from 'common/errors/Cancelled'
import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { BaseError } from 'src/editor/api/errors/base-error'

export interface UseApiWrapperWithValidationProps<
  Response,
  RequestArgs extends unknown[],
  DoThrowError extends boolean = false,
> {
  requestFunction: (...requestArgs: RequestArgs) => Promise<Response>
  handleSuccess?: (response: Response, requestArgs: RequestArgs) => void
  handleError?: (error: Error) => void
  handleFinally?: () => void
  successToastMessage?: string
  doThrowError?: DoThrowError
}

export const useApiWrapperWithValidation = <
  Response,
  RequestArgs extends unknown[],
  DoThrowError extends boolean = false,
>({
  requestFunction,
  handleSuccess,
  handleError,
  handleFinally,
  successToastMessage,
  doThrowError,
}: UseApiWrapperWithValidationProps<Response, RequestArgs, DoThrowError>) => {
  const { t } = useTypedTranslation()

  const [numberOfRequests, setNumberOfRequests] = useState<number>(0)
  const [isFetching, setIsFetching] = useState<boolean>(false)

  useEffect(() => setIsFetching(!!numberOfRequests), [numberOfRequests])

  const fetcher = useCallback(
    async (...requestArgs: RequestArgs) => {
      try {
        setNumberOfRequests(prev => prev + 1)
        const response = await requestFunction(...requestArgs)
        handleSuccess?.(response, requestArgs)
        successToastMessage && toast.success(successToastMessage)
        return response
      } catch (error) {
        if (error instanceof NetworkError) {
          toast.error(t('core.errors.no_connection'))
        } else if (error instanceof Cancelled) {
          // NOTE: do nothing
        } else if (error instanceof BaseError) {
          toast.error(() => (
            <div
              className="[&>a]:text-textLink [&>a]:hover:text-textLinkHover [&>a]:focus:text-textLinkPressed"
              dangerouslySetInnerHTML={{
                __html: error?.response?.detail,
              }}
            />
          ))
        } else {
          // biome-ignore lint/suspicious/noConsole: <explanation>
          console.log(`--error--`, error)
        }
        handleError?.(error as Error)
        if (doThrowError) {
          throw error
        }
      } finally {
        setNumberOfRequests(prev => prev - 1)
        handleFinally?.()
      }
    },
    [
      t,
      handleSuccess,
      handleError,
      successToastMessage,
      requestFunction,
      handleFinally,
    ],
  ) as (
    ...args: RequestArgs
  ) => Promise<DoThrowError extends true ? Response : Response | undefined>

  return {
    isFetching,
    fetcher,
  }
}
